/** @jsxImportSource @emotion/react */
import React from 'react';
import { Button, Card, Tag, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import planningArt from '../../assets/home/planning.webp';

const { Title, Paragraph, Text } = Typography;

const styles = {
  card: css({
    height: '100%',
    overflow: 'hidden',
    background: 'linear-gradient(155deg, #FFFCF0 0%, #FFF6DC 60%, #FDF0CE 100%)',
    border: '1px solid #F0DFAE',
  }),
  inner: css({ position: 'relative' }),
  body: css({
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minHeight: 220,
    position: 'relative',
    zIndex: 1,
    // Правая треть отдана иллюстрации — текст туда не заходит.
    paddingRight: '32%',
    '@media (max-width: 768px)': { paddingRight: 20 },
  }),
  art: css({
    position: 'absolute',
    right: -16,
    bottom: -20,
    width: 'min(50%, 340px)',
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 0,
    '@media (max-width: 768px)': { display: 'none' },
  }),
  topRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  }),
  title: css({ marginBottom: '0 !important', color: '#6B5411' }),
  countRow: css({ display: 'flex', alignItems: 'baseline', gap: 8 }),
  countNumber: css({ fontSize: 28, fontWeight: 700, color: '#8D6E00' }),
  spacer: css({ flex: 1 }),
};

type PlanningCardProps = {
  planCount?: number;
  description?: string;
  onOpen?: () => void;
  ctaLabel?: string;
  disabled?: boolean;
  loading?: boolean;
};

const PlanningCard: React.FC<PlanningCardProps> = ({
  planCount,
  description,
  onOpen,
  ctaLabel,
  disabled,
  loading,
}) => {
  const { t } = useTranslation('layout');
  const showCount = typeof planCount === 'number';

  return (
    <Card css={styles.card} styles={{ body: { padding: 0 } }} variant="borderless">
      <div css={styles.inner}>
        <img src={planningArt} alt="" css={styles.art} />
        <div css={styles.body}>
          <div css={styles.topRow}>
            <Title level={3} css={styles.title}>
              {t('home.planning.title')}
            </Title>
            {showCount ? (
              <Tag color="gold" style={{ fontWeight: 600 }}>
                {t('home.planning.plans', { count: planCount })}
              </Tag>
            ) : null}
          </div>

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {description || t('home.planning.description')}
          </Paragraph>

          {showCount && planCount > 0 ? (
            <div css={styles.countRow}>
              <span css={styles.countNumber}>{planCount}</span>
              <Text type="secondary">{t('home.planning.activePlans', { count: planCount })}</Text>
            </div>
          ) : (
            <Text type="secondary">{t('home.planning.summarySoon')}</Text>
          )}

          <div css={styles.spacer} />

          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            onClick={onOpen}
            disabled={disabled || !onOpen}
            loading={loading}
            style={{ alignSelf: 'flex-start', background: '#E0A82E', borderColor: '#E0A82E' }}
          >
            {ctaLabel || t('home.planning.openPlans')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default PlanningCard;
